/**
 * @typedef {Object} RangeContext
 * @property {Element} component
 * @property {Element} range
 * @property {HTMLInputElement} minSlider
 * @property {HTMLInputElement} maxSlider
 * @property {HTMLElement} fill
 * @property {HTMLElement} label
 * @property {HTMLElement} minLabel
 * @property {HTMLInputElement} minInput
 * @property {HTMLInputElement} maxInput
 * @property {number} rangeMax
 * @property {string} currencySymbol
 */

/**
 * @param {string | number} value
 * @returns {number}
 */
const parseNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Checks if the current viewport is mobile
 * @returns {boolean}
 */
const isMobile = () => window.innerWidth <= 640;

/**
 * @param {HTMLElement} slider
 * @returns {'min' | 'max'}
 */
const getSliderType = (slider) =>
  slider.classList.contains('price-range__slider--min') ? 'min' : 'max';

/**
 * @param {HTMLElement} slider
 * @returns {RangeContext | null}
 */
const getRangeContext = (slider) => {
  const range = slider.closest('.price-range');
  if (!range) return null;

  const component = slider.closest('price-facet-component[data-price-range]');
  if (!component) return null;

  const minSlider = range.querySelector('.price-range__slider--min');
  const maxSlider = range.querySelector('.price-range__slider--max');
  const fill = range.querySelector('[data-range-fill]');
  const label = range.querySelector('[data-range-label]');
  const minLabel = range.querySelector('.price-range__labels .price-range__label');
  const minInput = component.querySelector('[ref="minInput"]');
  const maxInput = component.querySelector('[ref="maxInput"]');

  if (
    !(minSlider instanceof HTMLInputElement) ||
    !(maxSlider instanceof HTMLInputElement) ||
    !(fill instanceof HTMLElement) ||
    !(label instanceof HTMLElement) ||
    !(minLabel instanceof HTMLElement) ||
    !(minInput instanceof HTMLInputElement) ||
    !(maxInput instanceof HTMLInputElement)
  ) {
    return null;
  }

  const rangeMax = parseNumber(maxSlider.max);
  const currencySymbol = label.dataset.currencySymbol || '$';

  return {
    component,
    range,
    minSlider,
    maxSlider,
    fill,
    label,
    minLabel,
    minInput,
    maxInput,
    rangeMax,
    currencySymbol
  };
};

/**
 * Updates the visual range UI (fill track, labels, constraints)
 * @param {RangeContext} context - The range context
 * @param {'min' | 'max'} source - Which slider triggered the update
 */
const updateRangeUI = (context, source) => {
  const { minSlider, maxSlider, fill, label, minLabel, rangeMax, currencySymbol } = context;
  let minValue = parseNumber(minSlider.value);
  let maxValue = parseNumber(maxSlider.value);

  // Enforce min/max constraints without moving the other slider
  if (minValue > maxValue) {
    if (source === 'min') {
      minValue = maxValue;
      minSlider.value = String(minValue);
    } else {
      maxValue = minValue;
      maxSlider.value = String(maxValue);
    }
  }

  // Update fill track position
  if (rangeMax > 0) {
    const minPercent = (minValue / rangeMax) * 100;
    const maxPercent = (maxValue / rangeMax) * 100;
    fill.style.left = `${minPercent}%`;
    fill.style.width = `${Math.max(maxPercent - minPercent, 0)}%`;
  } else {
    fill.style.left = '0%';
    fill.style.width = '0%';
  }

  // Update slider constraints
  minSlider.max = String(maxValue);
  maxSlider.min = String(minValue);

  // Update labels
  minLabel.textContent = `${currencySymbol}${Math.round(minValue)}`;
  label.textContent = `${currencySymbol}${Math.round(maxValue)}`;
};

/**
 * Applies the range filter by updating hidden inputs and triggering events
 * @param {RangeContext} context - The range context
 */
const applyRangeFilter = (context) => {
  const { component, minSlider, maxSlider, minInput, maxInput, rangeMax } = context;
  const minValue = parseNumber(minSlider.value);
  const maxValue = parseNumber(maxSlider.value);

  const finalMinValue = Math.min(minValue, maxValue);
  const finalMaxValue = Math.max(minValue, maxValue);

  const minInputValue = finalMinValue > 0 ? String(finalMinValue) : '';
  const maxInputValue = rangeMax > 0 ? String(finalMaxValue) : '';

  if (minInput.value !== minInputValue) {
    minInput.value = minInputValue;
  }
  if (maxInput.value !== maxInputValue) {
    maxInput.value = maxInputValue;
  }

  // On mobile, only trigger input events (for count update), not change event (for grid update)
  // The change event will be triggered when "Show Results" is clicked
  if (isMobile()) {
    minInput.dispatchEvent(new Event('input', { bubbles: true }));
    maxInput.dispatchEvent(new Event('input', { bubbles: true }));
    // Store that we have pending price filter changes
    if (component instanceof HTMLElement) {
      component.dataset.pendingPriceFilter = 'true';
    }
  } else {
    // Desktop: trigger immediately as before
    minInput.dispatchEvent(new Event('input', { bubbles: true }));
    maxInput.dispatchEvent(new Event('input', { bubbles: true }));
    component.dispatchEvent(new Event('change', { bubbles: true }));
  }
};

/**
 * Blocks clicks on the track (only allow dragging the dots)
 * @param {MouseEvent} event - The click event
 */
const blockTrackClick = (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (!target.classList.contains('price-range__slider')) return;

  // Prevent the slider from jumping to click position on track
  // Only allow interaction when dragging the actual thumb
  const rect = target.getBoundingClientRect();
  const clickX = event.clientX - rect.left;
  const thumbPosition = ((parseNumber(target.value) - parseNumber(target.min)) / 
                         (parseNumber(target.max) - parseNumber(target.min))) * rect.width;
  
  const thumbSize = 30;
  const thumbRadius = thumbSize / 2;
  
  // If click is not on the thumb, prevent default behavior
  if (Math.abs(clickX - thumbPosition) > thumbRadius) {
    event.preventDefault();
    event.stopPropagation();
  }
};

/**
 * Handles slider input events (while dragging)
 * @param {Event} event - The input event
 */
const handleSliderInput = (event) => {
  const slider = event.target;
  if (!(slider instanceof HTMLInputElement)) return;
  if (!slider.classList.contains('price-range__slider')) return;

  const context = getRangeContext(slider);
  if (!context) return;

  const sliderType = getSliderType(slider);
  updateRangeUI(context, sliderType);
};

/**
 * Handles slider change events (on release)
 * @param {Event} event - The change event
 */
const handleSliderChange = (event) => {
  const slider = event.target;
  if (!(slider instanceof HTMLInputElement)) return;
  if (!slider.classList.contains('price-range__slider')) return;

  const context = getRangeContext(slider);
  if (!context) return;

  const sliderType = getSliderType(slider);
  updateRangeUI(context, sliderType);
  applyRangeFilter(context);
};

/**
 * Detects which slider thumb is closer to the click position
 * @param {MouseEvent | TouchEvent} event - The pointer event
 * @param {RangeContext} context - The range context
 * @returns {HTMLInputElement} The closer slider
 */
const getCloserSlider = (event, context) => {
  const rect = context.range.getBoundingClientRect();
  const clientX = event instanceof TouchEvent ? event.touches[0]?.clientX : event.clientX;
  if (typeof clientX !== 'number') return context.maxSlider;
  
  const clickX = clientX - rect.left;
  const rangeWidth = rect.width;
  
  const minValue = parseNumber(context.minSlider.value);
  const maxValue = parseNumber(context.maxSlider.value);
  const rangeMin = parseNumber(context.minSlider.min);
  const rangeMax = parseNumber(context.maxSlider.max);
  
  if (rangeMax === rangeMin) return context.minSlider;
  
  const minThumbX = ((minValue - rangeMin) / (rangeMax - rangeMin)) * rangeWidth;
  const maxThumbX = ((maxValue - rangeMin) / (rangeMax - rangeMin)) * rangeWidth;
  
  return Math.abs(clickX - minThumbX) < Math.abs(clickX - maxThumbX) 
    ? context.minSlider 
    : context.maxSlider;
};

/**
 * Handles pointer events to bring the correct slider to front
 * @param {MouseEvent | TouchEvent} event - The pointer event
 */
const handlePointerMove = (event) => {
  const range = event.target instanceof Element ? event.target.closest('.price-range') : null;
  if (!range) return;
  
  const minSlider = range.querySelector('.price-range__slider--min');
  if (!(minSlider instanceof HTMLInputElement)) return;
  
  const context = getRangeContext(minSlider);
  if (!context) return;
  
  const closerSlider = getCloserSlider(event, context);
  context.minSlider.style.zIndex = closerSlider === context.minSlider ? '5' : '4';
  context.maxSlider.style.zIndex = closerSlider === context.maxSlider ? '5' : '4';
};

/**
 * Initializes all price ranges on page load
 */
const initializeRanges = () => {
  document.querySelectorAll('.price-range__slider--min').forEach((slider) => {
    if (!(slider instanceof HTMLInputElement)) return;
    const context = getRangeContext(slider);
    if (!context) return;

    updateRangeUI(context, 'min');
  });
};

document.addEventListener('input', handleSliderInput);
document.addEventListener('change', handleSliderChange);
document.addEventListener('mousemove', handlePointerMove);
document.addEventListener('mousedown', handlePointerMove, true);
document.addEventListener('mousedown', blockTrackClick, true);
document.addEventListener('click', blockTrackClick, true);
document.addEventListener('touchstart', blockTrackClick, true);
document.addEventListener('touchstart', handlePointerMove, true);
document.addEventListener('DOMContentLoaded', initializeRanges);
